import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import { ScreenStyles } from './AboutLook.styles';
import { ABOUT_LOOK_OPTIONS, ABOUT_LOOK_OPTIONS_MALE } from './constants';

const ASSETS_PATH = `${S3_BUCKET_URL}/layout-own-ai-multigender-forest/about-look`;

export const ScreenAboutLook: React.FC = () => {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'AboutLook',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const gender = form.watch('gender');

  const options =
    gender === 'male' ? ABOUT_LOOK_OPTIONS_MALE : ABOUT_LOOK_OPTIONS;
  const pronoun = gender === 'male' ? 'him' : 'her';

  const handleChange = (
    option: OnbOptionType<(typeof ABOUT_LOOK_OPTIONS)[number]['id']>,
  ) => {
    setSelectedId(option.id);
    form.setValue('about_look', option.id as LayoutDataType['about_look']);

    if (option.id === 'yes') {
      userService.createOrUpdate({
        onboarding_data: {
          about_look: option.id,
        },
      });
      nextStepTimeout(wizard.nextStep);
    } else {
      // Skip body_shape - set default value and go to next step after body_shape
      const defaultBodyShape = gender === 'male' ? 'tone' : 'regular';
      form.setValue('body_shape', defaultBodyShape);
      userService.createOrUpdate({
        onboarding_data: {
          about_look: option.id,
          body_shape: defaultBodyShape,
        },
      });

      nextStepTimeout(() => wizard.goToStep('obedience'));
    }
  };

  const bgImageSrc =
    gender === 'male' ? `${ASSETS_PATH}/male.png` : `${ASSETS_PATH}/female.png`;

  return (
    <ScreenStyles
      background="dark-solid"
      bgImage={<img src={bgImageSrc} alt="About Look" />}
      bgImageMask={false}
    >
      <OnboardingNavigation />

      <IonContent>
        <OwnAiTitle className="local-title">
          Would you like {pronoun} to look the way you like?
        </OwnAiTitle>

        <UiTypography
          className="local-subtitle"
          variant="body-large"
          color="neutral-white"
          align="center"
        >
          Make {pronoun} look exactly how you love
        </UiTypography>

        <OnboardingOptionsList>
          {options.map((option) => (
            <OnboardingOptionItem
              control={form.control}
              option={option}
              name="about_look"
              key={option.id}
              checked={selectedId === option.id}
              onChange={() => handleChange(option)}
              TEST_ID={`about_look-${option.id}`}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </ScreenStyles>
  );
};
